/**
 * Carousel component module.
 * @module shared/components/Carousel
 *
 */
import React, {Children, useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import styled, {withTheme, css} from 'styled-components/macro';
import {withSize} from 'react-sizeme';
import {Column, Row} from '../../shared';
import {Swiper, SwiperSlide} from 'swiper/react/swiper-react';
import SwiperCore, {Navigation, Pagination, Scrollbar} from 'swiper';
SwiperCore.use([Navigation, Pagination, Scrollbar]);

import 'swiper/swiper.min.css';
import 'swiper/swiper-bundle.min.css';
import './Carousel.css';
import {fill} from './cssUtils';

const _Row = styled(props => <Row {...props}/>)`
  margin-left: 6px;
`;

const _Blank = styled.div`
  height: 0px;
  width: 0px;
`;

const _RowDivider = styled.div`
  width: 1px;
  background-color: ${props => props.theme.dark ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.25)'};
  align-self: stretch;
  flex-grow: 0;
  flex-shrink: 0;
  margin: 5px 0 5px 5px;
`;

const _ColumnDivider = styled.div`
  ${fill(null)};
  height: 1px;
  background-color: ${props => props.theme.dark ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.25)'};
  margin: 5px 0 5px 0;
  align-self: stretch;
  flex-grow: 0;
  flex-shrink: 0;
`;

const _Carousel = styled.div`
  width: ${props => props.width ? props.width : '100%'};
  height: ${props => props.height ? props.height : '100%'};
  ${props => props.maxHeight && `max-height: ${props.maxHeight}`};
  ${props => props.minHeight && `min-height: ${props.minHeight}`};
  ${props => props.maxWidth && `max-width: ${props.maxWidth}`};
  ${props => props.minWidth && `min-width: ${props.minWidth}`};
  user-select: none;
  box-sizing: border-box;

  ${props => {
    const str = '+ .swiper-slide ';
    if (props.layout === 'column' && !props.showEdgeDividers && props.columnSlidesPerView) {
      return '.swiper-slide-active' + str.repeat(props.columnSlidesPerView - 1) + '> div > div.divider {visibility: hidden;}';
    }
    if (!props.showEdgeDividers && props.rowSlidesPerView) {
      return '.swiper-slide-active' + str.repeat(props.rowSlidesPerView - 1) + '> div > div.divider {visibility: hidden;}';
    }
  }}

  .swiper-slide-prev > div > div.divider {visibility: hidden;}

  .swiper-pagination-bullet {
    width: 10px;
    height: 10px;
    background: ${props => props.theme.dark ? 'rgba(255, 255, 255, 1)' : 'rgba(0, 0, 0, 1)'};
    ${props => props.theme.neumorphic ? css`
      box-shadow: inset 0.5px 0.5px 0px 0 rgba(255, 255, 255, 0.16), inset -0.5px -0.5px 0px 0 rgba(0, 0, 0, 0.5),
      1px 1px 4px 0px rgba(0, 0, 0, 0.5);
    ` : null}
  }

  .swiper-pagination-bullet-active {
    background: ${props => props.theme.dark ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.85)'};
    ${props => props.theme.neumorphic ? css`
      box-shadow: inset 0.5px 0.5px 0px 0 rgba(255, 255, 255, 0.5), inset -0.5px -0.5px 0px 0 rgba(0, 0, 0, 0.5),
      1px 1px 4px 0px rgba(0, 0, 0, 0.5);
    ` : null}
  }

  .swiper-scrollbar-drag {
    background: #484B50;
    ${props => props.theme.neumorphic ? css`
      background-image: linear-gradient(to bottom right, rgba(0, 0, 0, 0.09), rgba(0, 0, 0, 0.09));
      box-shadow: inset 1px 1px 0px 0 rgba(255, 255, 255, 0.7), inset -1px -1px 0px 0 rgba(0, 0, 0, 0.2)
    ` : null}
  }

  .swiper-button-prev::after {
    content: url("/icons/pagination_left_icon.svg")!important;
  }

  .swiper-button-next::after {
    content: url("/icons/pagination_right_icon.svg")!important;
  }
`;

const parsePx = str => parseInt(str.slice(0, -2), 10);

const Carousel = (props) => {
  const {children, size, childMinWidth, childMinHeight} = props;
  const childArr = [children].flat().filter(x => !!x);
  const [swiper, setSwiper] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [currentIdx, setCurrentIdx] = useState(0); // used to force update
  const [showEdgeDividers, setShowEdgeDividers] = useState(false);
  const [layout, setLayout] = useState('');

  const calcRowSlides = Math.floor(size.width / parsePx(childMinWidth));
  const calcColumnSlides = Math.floor(size.height / parsePx(childMinHeight));
  const rowSlidesPerView = childArr.length < calcRowSlides ? childArr.length : calcRowSlides;
  const columnSlidesPerView = childArr.length < calcColumnSlides ? childArr.length : calcColumnSlides;

  const swiperProps = {
    navigation: true,
    onSwiper: s => setSwiper(s),
    onSlideChange: () => {
      setCurrentIdx(swiper.activeIndex);
    },
    resistanceRatio: 0,
    centerInsufficientSlides: true,
    onSliderFirstMove: () => {
      setShowEdgeDividers(true);
    },
    onTouchEnd: () => {
      setShowEdgeDividers(false);
    },
  };

  const layoutProps = {
    row: {
      slidesPerView: rowSlidesPerView,
      enabled: false,
    },
    scroll: {
      slidesPerView: rowSlidesPerView,
      scrollbar: {
        hide: false,
        draggable: true,
      },
    },
    pagination: {
      slidesPerView: rowSlidesPerView,
      pagination: {
        clickable: true,
        dynamicBullets: true,
      },
    },
    column: {
      direction: 'vertical',
      slidesPerView: columnSlidesPerView,
      enabled: false,
    },
  };

  const viewWidth = size.width;
  const viewHeight = size.height;
  const contentWidth = childArr.length * parsePx(childMinWidth);
  const contentHeight = childArr.length * parsePx(childMinHeight);

  useEffect(() => {
    if (childArr.length === 1 || viewWidth >= contentWidth) {
      setLayout('row');
    } else if (rowSlidesPerView <= 1 && viewHeight >= contentHeight) {
      setLayout('column');
    } else if (rowSlidesPerView > 1) {
      setLayout('scroll');
    } else {
      setLayout('pagination');
    }
  }, [size]);

  const renderRow = () => Children.map(childArr, (child, idx) => [
    <>
      <SwiperSlide key={idx}>
        <_Row minWidth={childMinWidth}
              minHeight={childMinHeight}
              height={'100%'}
              wrap={false}
              overflow={'hidden'}
              justifySubRow={'space between'}>
          <_Blank>&nbsp;</_Blank>
          {child}
          <_RowDivider className='divider'/>
        </_Row>
      </SwiperSlide>
    </>,
  ].filter(c => !!c));

  return <_Carousel {...props}
                    layout={layout}
                    showEdgeDividers={showEdgeDividers}
                    rowSlidesPerView={rowSlidesPerView}
                    columnSlidesPerView={columnSlidesPerView}>
    {layout === 'row'
      ? <Swiper {...swiperProps} {...layoutProps.row}>
        {renderRow()}
      </Swiper>
      : null}
    {layout === 'scroll'
      ? <Swiper {...swiperProps} {...layoutProps.scroll}>
        {renderRow()}
      </Swiper>
      : null}
    {layout === 'column'
      ? <Swiper {...swiperProps} {...layoutProps.column}>
        {Children.map(childArr, (child, idx) => [
          <>
            <SwiperSlide key={idx}>
              <Column minWidth={childMinWidth}
                      minHeight={childMinHeight}
                      wrap={false}
                      height={'fill'}
                      justifySubColumn={'space between'}>
                <_Blank>&nbsp;</_Blank>
                {child}
                <_ColumnDivider className='divider'/>
              </Column>
            </SwiperSlide>
          </>,
        ].filter(c => !!c))}
      </Swiper>
      : null}
    {layout === 'pagination'
      ? <Swiper {...swiperProps} {...layoutProps.pagination}>
        {Children.map(childArr, (child, idx) => [
          <>
            <SwiperSlide key={idx}>
              <Column minWidth={childMinWidth}
                      minHeight={childMinHeight}
                      height={'fill'}
                      justifySubColumn={'middle'}
                      overflow={'hidden'}>
                {child}
              </Column>
            </SwiperSlide>
          </>,
        ].filter(c => !!c))}
      </Swiper>
      : null}
  </_Carousel>;
};

export default withTheme(withSize({monitorHeight: true, monitorWidth: true})(Carousel));

Carousel.propTypes = {
  theme: PropTypes.object.isRequired,
  children: PropTypes.element.isRequired,
  size: PropTypes.object,
  width: PropTypes.string,
  height: PropTypes.string,
  minWidth: PropTypes.string,
  minHeight: PropTypes.string,
  maxWidth: PropTypes.string,
  maxHeight: PropTypes.string,
  childMinWidth: PropTypes.string.isRequired,
  childMinHeight: PropTypes.string.isRequired,
};
